import React, { InputHTMLAttributes, forwardRef, ReactNode, useId, useState } from 'react';
import { AppIcon } from '../icons/AppIcon';
import { cn } from '@/lib/utils';

type InputSize = 'sm' | 'md' | 'lg';
type InputVariant = 'default' | 'filled';

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
    icon?: ReactNode;
    error?: string;
    label?: string;
    helperText?: string;
    containerClassName?: string;
    inputSize?: InputSize;
    variant?: InputVariant;
}

const sizeClasses: Record<InputSize, string> = {
    sm: 'input-sm',
    md: '',
    lg: 'input-lg',
};

const Input = forwardRef<HTMLInputElement, InputProps>(
    ({
        className,
        icon,
        error,
        label,
        helperText,
        containerClassName,
        inputSize = 'md',
        variant = 'default',
        type,
        disabled,
        id: idProp,
        name: nameProp,
        ...props
    }, ref) => {
        const generatedId = useId();
        const inputId = idProp ?? generatedId;
        const inputName = nameProp ?? inputId;
        const [showPassword, setShowPassword] = useState(false);
        const isPassword = type === 'password';
        const currentType = isPassword ? (showPassword ? 'text' : 'password') : type;
        const excludeFromAnalytics =
            isPassword || type === 'email' || type === 'tel';

        const inputEl = (
            <>
                {icon && <span className="input-icon">{icon}</span>}
                <input
                    ref={ref}
                    id={inputId}
                    name={inputName}
                    type={currentType}
                    disabled={disabled}
                    className={cn(
                        'input',
                        sizeClasses[inputSize],
                        variant === 'filled' && 'input-filled',
                        error && 'input-error-state',
                        isPassword && 'input--right-icon',
                        excludeFromAnalytics && 'ph-no-capture',
                        className,
                    )}
                    {...props}
                    {...(excludeFromAnalytics ? { 'data-ga-exclude': 'true' as const } : {})}
                />
                {isPassword && (
                    <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted transition-colors hover:text-default focus:outline-none"
                        tabIndex={-1}
                        aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                    >
                        {showPassword ? (
                            <AppIcon name="eye-off" className="h-4 w-4" />
                        ) : (
                            <AppIcon name="eye" className="h-4 w-4" />
                        )}
                    </button>
                )}
            </>
        );

        return (
            <div className={cn('input-wrap', containerClassName)}>
                {label && (
                    <label htmlFor={inputId} className="input-label">
                        {label}
                    </label>
                )}
                {icon || isPassword ? (
                    <div className={cn('input-group', icon && 'input-group--left-icon')}>{inputEl}</div>
                ) : (
                    inputEl
                )}
                {error && <p className="input-error">{error}</p>}
                {helperText && !error && <p className="input-hint">{helperText}</p>}
            </div>
        );
    }
);

Input.displayName = 'Input';

export default Input;
